import { Form, Input, Modal, Select, Space, notification } from "antd";
import React, { ReactElement, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { unwrapResult } from "@reduxjs/toolkit";
import moment from "moment";
import { RootDispatch } from "../../app/store";
import { getMembersBaseInfo, submitMemberPointExchangeRecord } from "./MembersManagementPageSlice";
import { getGenderName } from "../../models/user";
import { getAge } from "../../utils/StringUtils";
import styles from "./MembersManagementPage.module.css";
import { ExchangeItemSelect } from "./ExchangeItemSelect";
import { getMaskPhone } from "../../models/phone";

interface ExchangeModalProps {
  visible: boolean;
  membersId: number;
  QXData: any;
  onCancel: () => void;
  onOk: (values: any) => void;
}

export const ExchangeModal = ({
  visible,
  membersId,
  QXData,
  onCancel,
  onOk,
}: ExchangeModalProps): ReactElement => {
  const dispatch = useDispatch<RootDispatch>();
  const [form] = Form.useForm();
  const [category, setCategory] = useState("goods");
  const [selectedExchangeItem, setSelectedExchangeItem] = useState<any>(null);
  const [memberInfo, setMemberInfo] = useState<any>(null);
  const [quantity, setQuantity] = useState("1");
  const [confirmLoading, setConfirmLoading] = useState(false);

  // 计算年龄展示文本
  const getAgeDisplay = (birthday?: string) => {
    if (!birthday) return "-";
    const date = moment(birthday);
    if (!date.isValid()) return "-";
    const duration = moment.duration(moment().diff(date));
    if (duration.years() < 0) return "-";
    return getAge(duration.years(), duration.months() || 0, "岁");
  };

  // 计算商品剩余库存展示文本（与添加商品弹窗逻辑一致）
  const getStockText = (item: any) => {
    const totalCount = Number(item?.totalCount ?? 0)
    const totalSplitCount = Number(item?.totalSplitCount ?? 0)
    const accountUnit = item?.accountUnit ?? ''
    const splitUnit = item?.splitUnit ?? ''
    // totalSplitCount > 0（含两者都>0）：展示 总量+单位 拆分量+拆分单位
    if (totalSplitCount > 0) {
      return `${totalCount}${accountUnit}${totalSplitCount}${splitUnit}`
    }
    // 只有 totalCount > 0：只展示总量+单位
    if (totalCount > 0) {
      return `${totalCount}${accountUnit}`
    }
    return '-'
  };

  // 弹窗打开时获取会员详情
  useEffect(() => {
    if (visible && membersId) {
      dispatch(getMembersBaseInfo({ memberId: membersId }))
        .then(unwrapResult)
        .then((res: any) => {
          setMemberInfo({
            ...res,
            ageDisplay: getAgeDisplay(res?.birthday),
          });
        })
        .catch(() => setMemberInfo(null));
    }
  }, [visible, membersId]);

  const handleOk = () => {
    if (!selectedExchangeItem) {
      notification.error({ message: "请选择要兑换的项目" });
      return;
    }
    form
      .validateFields()
      .then((values) => {
        // 商品兑换数量不能超过当前库存
        if (category === "goods") {
          const stock = Number(selectedExchangeItem?.totalCount ?? 0);
          const exchangeNumber = Number(quantity) || 0;
          if (exchangeNumber > stock) {
            notification.error({ message: "库存不足，不支持兑换" });
            return;
          }
        }
        const totalPoint =
          (selectedExchangeItem?.point ?? 0) * (Number(quantity) || 0);
        if (totalPoint > (Number(memberInfo?.point) || 0)) {
          notification.error({ message: "积分不足，不支持兑换" });
          return;
        }
        const body = {
          memberId: membersId,
          type: 0,
          tenantCatalogId:
            selectedExchangeItem?.tenantCatalogId ?? selectedExchangeItem?.id,
          catalogType: category === "goods" ? 0 : 1,
          number: Number(quantity),
          point: totalPoint,
          remark: values.remark || "",
        };
        setConfirmLoading(true);
        dispatch(submitMemberPointExchangeRecord(body))
          .then(unwrapResult)
          .then(() => {
            notification.success({ message: "兑换成功" });
            onOk(body);
          })
          .catch(() => {
            notification.error({ message: "兑换失败" });
          })
          .finally(() => setConfirmLoading(false));
      })
      .catch((info) => {
        console.log("校验失败:", info);
      });
  };

  return (
    <Modal
      title={<span className={styles.exchangeModalTitle}>兑换项目</span>}
      visible={visible}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={confirmLoading}
      afterClose={() => {
        form.resetFields()
        setSelectedExchangeItem(null)
        setQuantity("1")
        setCategory("goods")
      }}
      okText="确定"
      cancelText="取消"
      width={680}
      destroyOnClose
      maskClosable={false}
    >
      {/* 顶部用户信息卡片 */}
      <div className={styles.exchangeUserCard}>
        <span className={styles.exchangeUserName}>
          {memberInfo?.memberName ?? "-"}
        </span>
        <span>
          {memberInfo?.sexType != null
            ? getGenderName(Number(memberInfo.sexType))
            : "-"}
        </span>
        <span>{memberInfo?.ageDisplay ?? "-"}</span>
        <span>{memberInfo?.memberPhone ? (!QXData ? getMaskPhone(memberInfo?.memberPhone) : memberInfo?.memberPhone) : ''}</span>
        <div>
          积分{" "}
          <span className={styles.exchangePointsValue}>
            {memberInfo?.point ?? 0}
          </span>
        </div>
      </div>

      {/* 表单区域 */}
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 20 }}
        initialValues={{
          category: "goods",
          quantity: "1",
          remark: "",
        }}
        onValuesChange={(changed: any) => {
          if ("quantity" in changed) setQuantity(changed.quantity);
        }}
      >
        {/* 兑换项目 */}
        <Form.Item label="兑换项目" required style={{ marginBottom: 18 }}>
          <Space size="middle" align="center">
            <Form.Item
              name="category"
              noStyle
              rules={[{ required: true, message: "请选择类型" }]}
            >
              <Select
                style={{ width: 110 }}
                onChange={(v) => {
                  setCategory(v)
                  setQuantity("1")
                  form.setFieldsValue({ quantity: "1" })
                }}
              >
                <Select.Option value="goods">商品</Select.Option>
                <Select.Option value="treatment">诊疗</Select.Option>
              </Select>
            </Form.Item>

            <ExchangeItemSelect
              category={category as "goods" | "treatment"}
              value={selectedExchangeItem}
              memberPoint={memberInfo?.point}
              onChange={(item) => setSelectedExchangeItem(item)}
            />

            {category === "goods" && selectedExchangeItem && (
              <span className={styles.exchangeStock}>
                剩余库存：<strong>{getStockText(selectedExchangeItem)}</strong>
              </span>
            )}
          </Space>
        </Form.Item>

        {/* 兑换数量 */}
        <Form.Item
          name="quantity"
          label="兑换数量"
          rules={[
            { required: true, message: "请输入兑换数量" },
            { pattern: /^[1-9]\d{0,2}$|^1000$/, message: "请输入1-1000的数字" },
          ]}
        >
          <Input
            style={{ width: 140 }}
            suffix={
              category === "goods" && selectedExchangeItem
                ? (selectedExchangeItem?.accountUnit || "")
                : undefined
            }
          />
        </Form.Item>

        {/* 使用积分 */}
        <Form.Item label="使用积分">
          <span className={styles.exchangePointsUsed}>
            {(selectedExchangeItem?.point ?? 0) * (Number(quantity) || 0)}
          </span>
        </Form.Item>

        {/* 备注 */}
        <Form.Item name="remark" label="备注">
          <Input maxLength={100} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};
